/**
 * UI-facing preset tables and matching rules, ported from MARA's web UI:
 *  - vendor-ref/mara/app/ui/src/store/presets.js:4-28 (display names, icons, VOLATILE_FIELDS)
 *  - vendor-ref/mara/app/ui/src/utils/nanome/enums.js:32-48 (hydrogen modes, surface opacity, identifiers)
 *  - vendor-ref/mara/app/ui/src/components/nanome/ComponentVisualizations.vue:53-71 (name matching)
 *  - vendor-ref/mara/app/ui/src/components/nanome/ComponentColoring.vue:23-35 (coloring matching)
 *  - vendor-ref/mara/app/ui/src/utils/enums.js:40-73 (file extensions)
 * The preset values themselves come from @nanome-lite/look (generated from GET /schemas); nothing is re-typed here.
 */
import { COLORING_PRESETS, VISUALIZATION_PRESETS, type ColoringPresetName, type VisualizationPresetName } from '@nanome-lite/look';
import type { ColoringModel, HydrogenRenderMode, VisualizationModel } from './dto';

/** presets.js:4-15 — display name -> /schemas coloring key, in menu order. */
export const COLORING_NAMES: Readonly<Record<string, ColoringPresetName>> = {
  'Element Type': 'elementType',
  'Residue Type': 'residueType',
  'Chain Name': 'chainName',
  'Secondary Structure': 'secondaryStructure',
  'Potential Interactions': 'hydrogenBonding',
  'BFactor': 'bFactorColdToWarm',
  'Occupancy': 'occupancy',
  'Uniform': 'uniformWhite',
  'Chain Instance': 'chainInstance',
  'Electrostatic': 'electrostatic',
};

/** presets.js:17-25 — display name -> { /schemas visualization key, tabler icon }, in popover order. */
export const VISUALIZATION_NAMES: Readonly<Record<string, { key: VisualizationPresetName; icon: string }>> = {
  'Ball and Stick': { key: 'ballAndStick', icon: 'i-tabler-line' },
  'Stick': { key: 'stick', icon: 'i-tabler-slash' },
  'Wire': { key: 'wire', icon: 'i-tabler-hexagon' },
  'Van der Waals': { key: 'spacefill', icon: 'i-tabler-color-filter' },
  'Cartoon': { key: 'cartoon', icon: 'i-tabler-scribble' },
  'Residue Label': { key: 'residueLabel', icon: 'i-tabler-tag' },
  'Surface': { key: 'surface', icon: 'i-tabler-egg-fried' },
};

/** presets.js:28 — user-mutable in the UI, so excluded from presets for matching and applying. */
export const VOLATILE_FIELDS = ['visible', 'renderHydrogen', 'opacity', 'subsurface', 'wireframe'] as const;

/** enums.js:32-36 */
export const HYDROGEN_MODE: Readonly<Record<string, HydrogenRenderMode>> = { 'No H': 'None', 'Polar H': 'PolarOnly', 'All H': 'All' };
/** enums.js:38-43 */
export const SURFACE_OPACITY: Readonly<Record<string, number | 'wireframe'>> = { Wire: 'wireframe', Sheer: 0.15, Semi: 0.5, Opaque: 1.0 };
/** enums.js:45-48 */
export const VISUALIZATION_IDENTIFIER: Partial<Record<VisualizationModel['kind'], keyof VisualizationModel>> = { Interaction: 'interactionType', Ribbon: 'ribbonStyle' };

/** Extensions the data layer parses (packages/data/src/api.ts:28), each optionally followed by .gz. */
export const SUPPORTED_EXTS = ['pdb', 'ent', 'cif', 'mmcif', 'mcif', 'bcif', 'sdf', 'sd', 'mol', 'mol2', 'xyz', 'gro', 'pdbqt', 'pqr'] as const;
/** For <input accept>: ".pdb,.pdb.gz,..." */
export const ACCEPT_EXTS = SUPPORTED_EXTS.flatMap(e => [`.${e}`, `.${e}.gz`]).join(',');

export function splitExt(fileName: string): { base: string; ext: string; gz: boolean } {
  let name = fileName;
  let gz = false;
  if (/\.gz$/i.test(name)) { gz = true; name = name.slice(0, -3); }
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return { base: name, ext: '', gz };
  return { base: name.slice(0, dot), ext: name.slice(dot + 1).toLowerCase(), gz };
}

export function isSupportedFileName(fileName: string): boolean {
  return (SUPPORTED_EXTS as readonly string[]).includes(splitExt(fileName).ext);
}

/** A preset with the volatile fields removed (presets.js:52-54). */
export type VisualizationPreset = Omit<VisualizationModel, (typeof VOLATILE_FIELDS)[number]>;

export function stripVolatile(v: VisualizationModel): VisualizationPreset {
  const out: Record<string, unknown> = { ...v };
  for (const f of VOLATILE_FIELDS) delete out[f];
  return out as unknown as VisualizationPreset;
}

/** Display name -> stripped preset + icon, the shape presets.js:48-56 builds from /schemas. */
export const VISUALIZATION_MENU: ReadonlyArray<{ name: string; icon: string; preset: VisualizationPreset }> =
  Object.entries(VISUALIZATION_NAMES).map(([name, { key, icon }]) => ({
    name, icon, preset: stripVolatile(VISUALIZATION_PRESETS[key] as unknown as VisualizationModel),
  }));

/** Display name -> coloring preset, the shape presets.js:42-46 builds from /schemas. */
export const COLORING_MENU: ReadonlyArray<{ name: string; preset: ColoringModel }> =
  Object.entries(COLORING_NAMES).map(([name, key]) => ({ name, preset: COLORING_PRESETS[key] as unknown as ColoringModel }));

export function visualizationPreset(name: string): VisualizationPreset | undefined {
  return VISUALIZATION_MENU.find(m => m.name === name)?.preset;
}
export function coloringPreset(name: string): ColoringModel | undefined {
  return COLORING_MENU.find(m => m.name === name)?.preset;
}

/** ComponentVisualizations.vue:54-62 — every key of the preset (except coloring) must deep-equal the visualization's. */
function matches(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  return Object.keys(b as object).every((key) => {
    if (key === 'coloring') return true;
    return matches((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]);
  });
}

const spaceCamel = (s: string) => s.replace(/\B[A-Z]/g, ' $&');

/** ComponentVisualizations.vue:53-71 — preset display name, else the identifier (ribbonStyle/interactionType), else the kind. */
export function visualizationName(v: VisualizationModel): string {
  const hit = VISUALIZATION_MENU.find(m => matches(v, m.preset));
  if (hit) return hit.name;
  const id = VISUALIZATION_IDENTIFIER[v.kind];
  const idValue = id ? v[id] : undefined;
  if (typeof idValue === 'string') return spaceCamel(idValue);
  return spaceCamel(v.kind);
}

/** ComponentColoring.vue:23-35 — kind match, plus propertySource for Property kinds. */
export function coloringName(c: ColoringModel): string | null {
  const hit = COLORING_MENU.find(({ preset }) => {
    if (c.kind !== preset.kind) return false;
    if (c.kind === 'Property') return c.propertySource === preset.propertySource;
    return true;
  });
  return hit?.name ?? null;
}

/**
 * Port of Nanome.Domain Visualization.Is (Assets/Scripts/src/Nanome.Domain/Scenes/Components/Visualizations/
 * Visualization.cs:31-44, Atomistic.cs:79-91, Ribbon.cs:25-33, Interaction.cs:53-64, MolecularSurface.cs:12-15,
 * ResidueLabel.cs:16-19): same kind, same size; Atomistic also compares renderBond/bondScale; Ribbon the style;
 * Interaction the type, bondScale and gapScale. Coloring and visibility never take part.
 */
export function visualizationIs(a: Pick<VisualizationModel, 'kind' | 'size'> & Partial<VisualizationModel>, b: Pick<VisualizationModel, 'kind' | 'size'> & Partial<VisualizationModel>): boolean {
  if (a.kind !== b.kind) return false;
  if (a.size.sizeOption !== b.size.sizeOption || Math.abs(a.size.scale - b.size.scale) > 1e-6) return false;
  switch (a.kind) {
    case 'Atomistic': {
      const ab = a.renderBond ?? false, bb = b.renderBond ?? false;
      return ab ? bb && Math.abs((a.bondScale ?? 0) - (b.bondScale ?? 0)) < 1e-6 : !bb;
    }
    case 'Ribbon': return (a.ribbonStyle ?? 'Cartoon') === (b.ribbonStyle ?? 'Cartoon');
    case 'Interaction': return (a.interactionType ?? 'HydrogenBonds') === (b.interactionType ?? 'HydrogenBonds')
      && Math.abs((a.bondScale ?? 0) - (b.bondScale ?? 0)) < 1e-6 && Math.abs((a.gapScale ?? 0) - (b.gapScale ?? 0)) < 1e-6;
    default: return true;
  }
}

/** Atomistic.cs:93-96 — one of the four atomistic presets (any coloring / hydrogen mode). */
export function isDefaultAtomistic(v: VisualizationModel): boolean {
  if (v.kind !== 'Atomistic') return false;
  return (['ballAndStick', 'stick', 'wire', 'spacefill'] as const).some(k => visualizationIs(v, VISUALIZATION_PRESETS[k] as unknown as VisualizationModel));
}
